import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@core-ui/Components/ui/card.tsx';
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@core-ui/Components/ui/table.tsx';
import  { type ApiResponse, type CalendarResponse, type CalendarSlot, type CurrentUser } from '@types';
import { getCurrentUser } from '@features/auth';
import getCalendlyApi from '@features/Calendar/hooks/useCalendlyApi.ts';
import { writeErrorLog } from '@lib/logger/logError.ts';
import { Skeleton } from '@core-ui/Components/ui/skeleton.tsx';
import { Calendar } from '@core-ui/Components/ui/calendar.tsx';
import { type Locale } from 'react-day-picker';
import { enUS, es } from 'date-fns/locale';
import { DailySkeleton, RenderEvents } from '@features/Calendar/components/dailyEvents.tsx';
import moment from 'moment-timezone';

function MonthlySkeleton ({ rows = 6 }: { rows?: number }): React.JSX.Element {
  return (
    <>
      <Table>
        <TableHeader className="bg-muted/60">
          <TableRow className="h-[55px] border-b border-border/60">
            <TableHead colSpan={5} className="text-center text-lg font-bold">
              <Skeleton className="h-8 w-40 rounded-lg bg-muted-foreground/20 justify-self-center" />
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          <TableRow className="border-transparent hover:bg-muted/30 align-center">
            {Array.from({ length: 5 }).map((_, i) => (
              <TableCell key={i} aria-colSpan={5}>
                <Skeleton
                  key={i}
                  className="h-6 w-24 rounded-lg bg-muted-foreground/20 justify-self-center mt-4 mb-4"
                />
              </TableCell>
            ))}
          </TableRow >
          {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i} className="border-transparent hover:bg-muted/30" style={{ height: `${62 / rows}vh` }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableCell key={i}>
                  <div className="flex justify-center">
                    <Skeleton className="h-16 w-16 rounded-full bg-muted-foreground/20" />
                  </div>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

const CalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const user: CurrentUser | null = getCurrentUser();
  const [groupedSlots, setGroupedSlots] = useState<Record<string, CalendarSlot[]>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [date, setDate] = React.useState<Date>(new Date());
  const localeMap: Record<string, Locale> = {
    en: enUS,
    es
  };
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const [isCalendarHeight, setCalendarHeight] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  useEffect(() => {
    if (calendarRef.current !== null) {
      setCalendarHeight(calendarRef.current.offsetHeight);
    }
  }, [date, selectedDate, loading, currentMonth]);
  

  useEffect(() => {
    const fetchCalendarData = async (): Promise<void> => {
      if (user?.email !== '') {
        setLoading(true);
        try {
          const data: ApiResponse<CalendarResponse> = await getCalendlyApi((user?.userId ?? ''), false);
          console.log(data, 'data');

          if (data?.data?.data !== undefined && Array.isArray(data.data.data)) {
            const validSlots: CalendarSlot[] = data.data.data.map((slot) => ({
              ...slot,
              start: slot.start,
              end: slot.end,
            }));

            const isoDate = data.data?.date;
            if (isoDate != null) {
              const userTimezone = user?.timezone ?? 'UTC';
              const momentDate = moment.utc(isoDate).tz(userTimezone);

              const formatted = formatDate(isoDate, userTimezone);
              setSelectedDate(formatted);
              setDate(momentDate.toDate());
              setCurrentMonth(momentDate.toDate());
            }

            setGroupedSlots(processCalendarData(validSlots, user?.timezone ?? 'UTC'));
          }
            
        } catch (error) {
          await writeErrorLog(
            'Error fetching Calendly events. Please try again later.',
            'Calendar page',
            {}
          );
        } finally {
          setLoading(false);
        }
      }
    };
    fetchCalendarData().catch(() => {
      setLoading(false);
    });
  }, [user?.email, user?.timezone]);

  // Group calendar data by date
  const processCalendarData = (data: CalendarSlot[], userTimezone: string): Record<string, CalendarSlot[]> => {

    return data.reduce<Record<string, CalendarSlot[]>>((acc, slot) => {
      // Parsing dates as UTC as default data from server
      const momentInUserTz = moment.utc(slot.start, 'M/D/YYYY, h:mm:ss A').tz(userTimezone);
      const localDate = momentInUserTz.format('M/D/YYYY');

      if (acc[localDate] == null) {
        acc[localDate] = [];
      }
      acc[localDate].push(slot);
      return acc;
    }, {});
  };

  function formatDate (apiDate: string, userTimezone: string = 'UTC'): string {
    return moment.utc(apiDate).tz(userTimezone).format('M/D/YYYY');
  }

  function capitalizeFirstLetter (str: string): string {
    if (str === '' || str === null) {
      return '';
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const goToToday = (): void => {
    const userTimezone = user?.timezone ?? 'UTC';
    const todayInUserTz = moment().tz(userTimezone);

    const todayDate = todayInUserTz.toDate();
    const todayFormatted = todayInUserTz.format('M/D/YYYY');

    setDate(todayDate);
    setSelectedDate(todayFormatted);
    setCurrentMonth(todayDate);
  };

  const eventDays = Object.keys(groupedSlots)
    .map((d) => new Date(d))
    .filter((d) => !isNaN(d.getTime()));

  const modifiers = {
    hasEvents: eventDays,
  };

  const modifiersClassNames = {
    hasEvents: '[&>button]:bg-primary/20 [&>button]:hover:bg-primary/10 [&>button]:rounded-full'
  };
    
  return (
    <Card className="p-6 rounded-2xl shadow-lg border border-border bg-card text-foreground">
      <CardHeader className="px-0">
        <CardTitle data-qa-id="calendar-title" className="text-2xl font-bold text-foreground">{t('calendar.title', 'Calendar')}</CardTitle>
        <CardDescription className="mt-1">{t('calendar.description')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col md:flex-row px-0">
        <div ref={calendarRef} className="w-full md:w-[57vw] h-full overflow-auto rounded-md border border-border bg-card" data-qa-id="calendar-table">
          {loading ? (<MonthlySkeleton /> ) : (
            <Calendar
              mode="single"
              selected={date}
              month={currentMonth}
              onSelect={(d) => {
                if (d == null) {
                  return;
                }
                setDate(d);
                setSelectedDate(d.toLocaleDateString('en-US'));
              }}
              onMonthChange={(month) => {
                setCurrentMonth(month);
              }}
              locale={localeMap[i18n.language] ?? enUS}
              className="w-full rounded-lg p-0"
              modifiers={modifiers}
              modifiersClassNames={modifiersClassNames}
              formatters={{
                formatWeekdayName: (day) =>
                  capitalizeFirstLetter(
                    day.toLocaleDateString(i18n.language, { weekday: 'short' })
                  ),
                formatCaption: (date) =>
                  capitalizeFirstLetter(date.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' }))
              }}
            />
          )}
        </div>

        <div className="h-[3vh] md:w-[3vw]"></div>

        <div className="w-full md:w-[40vw] rounded-md border border-border bg-card overflow-hidden" style={{ height: (isCalendarHeight != null) ? `${isCalendarHeight}px` : 'h-full' }}>
          
          {loading ? (<DailySkeleton/> ) : (<RenderEvents groupedSlots={groupedSlots} selectedDate={selectedDate} height={isCalendarHeight} clickBtn={goToToday} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarPage;